import Configuration from '@/agilis/util/Configuration'
import DataTableV2Title from '@/agilis/components/DataTableV2/DataTableV2Title/DataTableV2Title.vue'
import FormSelectMultiple from '@/agilis/components/Form/FormSelectMultiple/FormSelectMultiple.vue'
import toursMixin from '@/agilis/components/Tour/mixins/toursMixin'

import L from 'lodash'
import { mapActions, mapGetters } from 'vuex'
import ErrorUtils from '@/agilis/util/Error'

export default {
  name: 'DataTableV2ItemEdit',
  mixins: [toursMixin],

  props: {
    api: String,
    propTitle: String,
    propSubtitle: String,
    mode: String,
    itemContext: {
      type: Object,
      default: () => {
        return {
          trackerId: null,
          applicationId: null,
          workspaceId: null,
          trackerTypeId: null
        }
      },
      required: false
    },
    configuration: Object
  },

  data () {
    return {
      itemDTO: {},
      formCustomComponents: {
        FormSelectMultiple
      },
      trackerReady: false // will be set ready in mounted
    }
  },

  components: {
    DataTableV2Title
  },

  mounted: async function () {
    this.itemDTO = Configuration.datatable.getDTO(this.datatable)
    L.merge(this.itemDTO, {
      applicationId: this.applicationId,
      workspaceId: this.workspaceId,
      trackerTypeId: this.trackerTypeId
    })
    if (this.mode === 'edit') {
      await this.fetchData()
    } else if (this.mode === 'insert') {
      const itemDTO = this.$router.agilis.getRouteData()
      itemDTO && L.merge(this.itemDTO, itemDTO)
      Configuration.datatable.mapItemDTOForEdit(this.datatable, this.itemDTO)
    }
    this.trackerReady = true
  },

  computed: {
    ...mapGetters({
      contextApplicationId: 'contextStore/applicationId',
      contextWorkspaceId: 'contextStore/workspaceId',
      contextTrackerTypeId: 'contextStore/trackerTypeId',
      trackerType: 'contextStore/trackerType',
      trackerTypes: 'contextStore/trackerTypes',
      contextTrackerId: 'contextStore/trackerId',
      loading: 'loaderStore/loading',
      selectedContext: 'contextStore/selectedContext'
    }),
    applicationId () {
      return this.itemContext.applicationId || this.contextApplicationId
    },
    workspaceId () {
      return this.itemContext.workspaceId || this.contextWorkspaceId
    },
    trackerTypeId () {
      return this.itemContext.trackerTypeId || this.contextTrackerTypeId
    },
    trackerId () {
      return this.itemContext.trackerId || this.contextTrackerId
    },
    editViaQuery () {
      return !!this.configuration.queryEditMode
    },

    datatable () {
      if (this.editViaQuery) {
        const queryId = this.configuration.queryEditMode

        // Which datatable definition to use?
        // 1- datatable defined specifically for query in edit mode: "queryEditModeDatatable"
        // 2- datatable defined generically for query (i.e: share between select/edit/view): "queryDatatable"
        // 3- datatable defined inside the query: the datatable used on BE side
        const dataTableFE = this.configuration.queryEditModeDatatable || this.configuration.queryDatatable
        const { dataTable: dataTableBE } = Configuration.getApplications()[this.applicationId]?.query[queryId]

        return dataTableFE || dataTableBE
      }

      return this.configuration
    },

    formFields () {
      const datatableColumns = L.cloneDeep(this.datatable.columns)
      for (const colConfig of Object.values(datatableColumns)) {
        if (colConfig.url) { // insert tracker context in api paths where necessary
          colConfig.url = this.$axios.agilisUrlAddContext(colConfig.url, this.selectedContext)
        }
        if (colConfig.api) {
          colConfig.api.url = this.$axios.agilisUrlAddContext(colConfig.api.url, this.selectedContext)
        }
      }

      // Form graphical elements
      const formElements = this.datatable.formElements

      return {
        ...datatableColumns,
        ...formElements
      }
    },

    trackerForRendering () {
      return Configuration.datatable.mapItemDTO(this.datatable, this.itemDTO)
    },

    computedTitle () {
      if (this.mode === 'insert') {
        return this.propTitle
      }

      if (this.configuration?.itemTitle) {
        return this.$t(this.configuration.itemTitle, this.trackerForRendering)
      }

      return this.propTitle + ' ' + this.trackerId
    },

    computedSubtitle () {
      return this.propSubtitle
    }
  },

  methods: {
    ...mapActions({
      'notifyError': 'notificationStore/notifyError',
      'startLoader': 'loaderStore/startLoader',
      'stopLoader': 'loaderStore/stopLoader'
    }),

    beforeChange (delta) {
      return delta
    },

    async fetchData () {
      if (this.editViaQuery) {
        return this.fetchDataViaQuery()
      }

      this.setLoader(true)
      const url = this.$axios.agilisUrlDataTableEdit(this.api, this.trackerId)

      try {
        const response = await this.$axios.get(
          url,
          this.$axios.agilisRequestConfig(url))
        this.onFetchResponse(response.data)
      } catch (error) {
        this.notifyError(error)
      }
      this.setLoader(false)
    },

    /**
     * Fetch data calling the query API to retrieve the entity joined with other entities
     */
    async fetchDataViaQuery (itemId) {
      this.setLoader(true)

      const url = this.$axios.agilisUrlQuery(this.applicationId, this.configuration.queryEditMode)
      const filterById = { field: 'id', operator: 'EQUALS', value: this.trackerId }
      const body = Configuration.datatable.paginatedRequestBody(this.datatable, {}, '', [filterById], [])
      const config = this.$axios.agilisRequestConfig(url)

      try {
        const response = await this.$axios.post(url, body, config)
        this.onFetchResponse(response.data.items[0])
      } catch (error) {
        this.notifyError(error)
      }
      this.setLoader(false)
    },

    /**
     * Update model with fetch response: raw & mapped item
     */
    onFetchResponse (entity) {
      L.merge(this.itemDTO, entity)
      Configuration.datatable.mapItemDTOForEdit(this.datatable, this.itemDTO)
    },

    /**
     * Loader utility function
     */
    setLoader (value) {
      const loader = { id: 'edit-tracker' }
      if (value) this.startLoader(loader)
      else this.stopLoader(loader)
    },

    saveItem (itemDTO) {
      let savedModel

      try {
        savedModel = Configuration.datatable.mapItemDTOForSave(this.datatable, JSON.parse(JSON.stringify(this.itemDTO)))
      } catch (error) {
        this.notifyError(error)
        return
      }

      this.startLoader({ id: 'save-tracker' })
      if (this.mode === 'edit') {
        const url = this.$axios.agilisUrlDataTableEdit(this.api, this.trackerId)
        this.$axios
          .put(
            url,
            savedModel,
            this.$axios.agilisRequestConfig(url, null, { applicationId: this.applicationId, workspaceId: this.workspaceId }))
          .then((response) => {
            this.$emit('edit-done', this.itemDTO)
            if (!this.itemContext.trackerTypeId) {
              const routeCallback = this.$router.agilis.getRouteNextRoute()
              if (routeCallback && typeof routeCallback === 'function') {
                routeCallback()
              } else {
                this.$router.agilis.view({ trackerId: itemDTO.id })
              }
            }
          })
          .catch((error) => {
            const errorMessage = ErrorUtils.getErrorMessage(error, savedModel.trackerTypeId, this.applicationId)
            this.notifyError(errorMessage)
          })
          .finally(() => {
            this.stopLoader({ id: 'save-tracker' })
          })
      } else {
        const url = this.$axios.agilisUrlDataTable(this.api)
        this.$axios
          .post(
            url,
            savedModel,
            this.$axios.agilisRequestConfig(url, null, { applicationId: this.applicationId, workspaceId: this.workspaceId }))
          .then((response) => {
            this.$emit('edit-done', response.data)
            if (!this.itemContext.trackerTypeId) {
              const routeCallback = this.$router.agilis.getRouteNextRoute()
              if (routeCallback && typeof routeCallback === 'function') {
                routeCallback()
              } else {
                this.$router.agilis.view({ trackerId: response.data.id })
              }
            }
          })
          .catch((error) => {
            const errorMessage = ErrorUtils.getErrorMessage(error, savedModel.trackerTypeId, this.applicationId)
            this.notifyError(errorMessage)
          })
          .finally(() => {
            this.stopLoader({ id: 'save-tracker' })
          })
      }
    },
    cancelItem (itemDTO, event) {
      if (!this.itemContext.trackerTypeId) {
        const routeCallback = this.$router.agilis.getRouteNextRoute()
        if (routeCallback && typeof routeCallback === 'function') {
          routeCallback()
        } else {
          this.itemDTO.id
            ? this.$router.agilis.view({ trackerId: this.itemDTO.id })
            : this.$router.agilis.viewTable()
        }
      }
      this.$emit('cancel')
    },

    handleSelectChange (item) {
      // copy selected item properties to DTO
      L.assign(this.itemDTO, item)
    },

    fieldTitle (colConfig) {
      return Configuration.datatable.getRequiredLabel(colConfig)
    },

    userCanAccessField (colId) {
      return Configuration.auth.userCanAccessField(this.selectedContext, colId)
    },

    userCanEditField (colId) {
      return Configuration.auth.userCanAccessField(this.selectedContext, colId, null, 'edit')
    }
  }

}
